/**
 * RealTimeMetricsDashboard - Live metrics dashboard with auto-refresh
 * Shows: SLA gauge, task charts, stock consumption, patient stats
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Package,
  Clock,
  Target,
  RefreshCw,
  Zap,
  AlertTriangle,
  CheckCircle,
  Calendar,
  DollarSign,
  Pause,
  Play
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const MetricsDashboard = () => {
  const { api, isAuthenticated } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connected, setConnected] = useState(false);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await api.get('/metrics/current');
      setMetrics(response.data);
      setLastUpdate(new Date());
      setConnected(true);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Initial load and auto-refresh
  useEffect(() => {
    if (!isAuthenticated) return;
    
    fetchMetrics();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchMetrics, 10000); // 10 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuthenticated, autoRefresh, fetchMetrics]);

  // SLA Gauge Component
  const SLAGauge = ({ value }) => {
    const getColor = (val) => {
      if (val >= 90) return '#10b981'; // green
      if (val >= 70) return '#f59e0b'; // amber
      return '#ef4444'; // red
    };

    return (
      <div className="relative flex flex-col items-center">
        <div className="relative w-40 h-20 overflow-hidden">
          <div 
            className="absolute w-40 h-40 rounded-full border-[16px] border-gray-200"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)' }}
          />
          <div 
            className="absolute w-40 h-40 rounded-full border-[16px] transition-all duration-1000"
            style={{ 
              borderColor: getColor(value),
              clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
              transform: `rotate(${(value / 100) * 180 - 180}deg)`,
              transformOrigin: 'center center'
            }}
          />
          <div className="absolute inset-0 flex items-end justify-center pb-2">
            <span className="text-3xl font-bold" style={{ color: getColor(value) }}>
              {value}%
            </span>
          </div>
        </div>
        <div className="flex gap-4 mt-2 text-xs">
          <span className="text-red-500">0%</span>
          <span className="text-amber-500">50%</span>
          <span className="text-green-500">100%</span>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="h-6 w-6 text-emerald-500" />
              Dashboard de Métricas em Tempo Real
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitoramento de desempenho operacional
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              {lastUpdate && (
                <span>Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}</span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={autoRefresh ? 'border-emerald-500' : ''}
            >
              {autoRefresh ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              {autoRefresh ? 'Pausar' : 'Retomar'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchMetrics} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {loading && !metrics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        ) : metrics ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* SLA Rate */}
              <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Target className="h-4 w-4 text-emerald-600" />
                    Taxa SLA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SLAGauge value={metrics.sla?.sla_rate || 0} />
                  <div className="flex justify-between text-xs mt-2 text-muted-foreground">
                    <span>No prazo: {metrics.sla?.on_time || 0}</span>
                    <span>Atrasadas: {metrics.sla?.late || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Active Admissions */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-500" />
                    Internações Ativas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-blue-600">
                    {metrics.patients?.active_admissions || 0}
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <Badge variant="outline" className="bg-green-50">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      +{metrics.patients?.new_admissions_month || 0} este mês
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Média: {metrics.patients?.avg_stay_days || 0} dias
                  </p>
                </CardContent>
              </Card>

              {/* Tasks Today */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Tarefas Hoje
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-amber-600">
                      {metrics.tasks?.today_completed || 0}
                    </span>
                    <span className="text-muted-foreground">/ {metrics.tasks?.today_created || 0}</span>
                  </div>
                  <Progress 
                    value={metrics.tasks?.today_created > 0 
                      ? (metrics.tasks.today_completed / metrics.tasks.today_created) * 100 
                      : 0
                    } 
                    className="mt-3"
                  />
                  <div className="flex justify-between text-xs mt-2 text-muted-foreground">
                    <span>Pendentes: {metrics.sla?.pending || 0}</span>
                    <span className="text-red-500">Vencidas: {metrics.sla?.overdue || 0}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Stock Consumption */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Package className="h-4 w-4 text-purple-500" />
                    Consumo de Estoque
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-purple-600">
                    {metrics.stock?.total_quantity || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">itens esta semana</p>
                  <div className="flex items-center gap-2 mt-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="font-medium">
                      R$ {(metrics.stock?.total_cost || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Média: R$ {(metrics.stock?.avg_daily_cost || 0).toFixed(2)}/dia
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Task Volume Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    Volume de Tarefas (7 dias)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={metrics.tasks?.daily_data || []}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="day_name" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255,255,255,0.95)', 
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="created" 
                        name="Criadas" 
                        stroke="#3b82f6" 
                        fill="#3b82f6" 
                        fillOpacity={0.3}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="completed" 
                        name="Concluídas" 
                        stroke="#10b981" 
                        fill="#10b981"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Stock Consumption Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="h-4 w-4 text-purple-500" />
                    Consumo de Estoque (7 dias)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={metrics.stock?.daily_data || []}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="day_name" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255,255,255,0.95)', 
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                        formatter={(value, name) => [
                          name === 'cost' ? `R$ ${value.toFixed(2)}` : value,
                          name === 'cost' ? 'Custo' : 'Quantidade'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="quantity" name="Quantidade" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Top Consumed Items */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top 5 Itens Consumidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(metrics.stock?.top_items || []).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        >
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.quantity} unidades</p>
                        </div>
                      </div>
                    ))}
                    {(!metrics.stock?.top_items || metrics.stock.top_items.length === 0) && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Sem dados de consumo
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Admissions by Origin */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Internações por Origem</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={metrics.patients?.by_origin || []}
                        dataKey="count"
                        nameKey="origin"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        label={({ origin, count }) => `${count}`}
                        labelLine={false}
                      >
                        {(metrics.patients?.by_origin || []).map((_, idx) => (
                          <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend 
                        layout="vertical" 
                        align="right" 
                        verticalAlign="middle"
                        formatter={(value) => <span className="text-xs">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* SLA Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resumo SLA (30 dias)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">No prazo</span>
                      </div>
                      <span className="font-bold text-green-600">{metrics.sla?.on_time || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">Atrasadas</span>
                      </div>
                      <span className="font-bold text-amber-600">{metrics.sla?.late || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">Pendentes</span>
                      </div>
                      <span className="font-bold text-blue-600">{metrics.sla?.pending || 0}</span>
                    </div>
                    <div className="flex items-center justify-between border-t pt-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-sm font-medium">Vencidas</span>
                      </div>
                      <span className="font-bold text-red-600">{metrics.sla?.overdue || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <Card className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <p className="text-lg font-medium">Não foi possível carregar as métricas</p>
            <Button onClick={fetchMetrics} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MetricsDashboard;
